import { UseRoom, UseTag } from '@core/usecases';
import { Pagination, RoomEntity, RoomsListEntity } from '@core/entities';
import { FilterQuery, ScheduleColumn, ScheduleItem } from '@core/types';
import { ConfirmationDialogType, DeleteConfirmationOptions, NotificationType, RoomType, SocketEvents, TagType } from '@core/enums';
import { untilDestroyed } from '@ngneat/until-destroy';
import { DropDownItem } from '@core/types/Dropdown.type';
import { inject } from '@angular/core';
import { PermissionService } from '@auth';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTimeUtility, EnumToKeyValueArray, GetTeamId, GetUserId, isMobileView, removeNullAndUndefinedProperties, scrollToTop, timeRangeValidator } from '@core/utils';
import { TranslateService } from '@ngx-translate/core';
import { FormService, Logger } from '@core/services';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { HotToastService } from '@ngxpert/hot-toast';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { instanceToPlain } from 'class-transformer';
import { SocketIoService } from '@core/socket-io';
import { ButtonComponent } from '@shared/components/ui';
import { forkJoin } from 'rxjs';
import { convertRoomsToScheduleData } from '@shared/compositions/helpers';

const log = new Logger('Rooms Composition');

export class RoomsComposition extends FormService {
  rooms: RoomEntity[] = [];
  pagination: Pagination = {
    page: 1,
    pageSize: 12,
  };
  roomsTags: DropDownItem[] = [];
  roomTypes: DropDownItem[] = [];
  filterQuery: FilterQuery = {
    tags: null,
    instructorType: null,
  };

  // Properties for CREATE/EDIT Room
  currentRoom: RoomEntity;
  days: DropDownItem[] = [];
  selectedImage: File;

  // ---- Specifically for Schedule View in instructors
  isScheduleView = false;
  roomScheduleColumns: ScheduleColumn[];
  roomScheduleItems: ScheduleItem<RoomEntity>[] = [];
  isLoadingForSchedule = true;

  protected readonly _translate = inject(TranslateService);
  protected readonly _bottomSheet = inject(NgxBottomSheetService);
  protected _dialogRef: DialogRef<any, boolean>;
  protected readonly _dialogService = inject(DialogService);
  protected readonly _socketService = inject(SocketIoService);
  protected readonly _activatedRoute = inject(ActivatedRoute);

  private readonly _useRoom = new UseRoom();
  private readonly _useTags = new UseTag();

  private readonly _permissionService = inject(PermissionService);
  private readonly _router = inject(Router);
  private readonly _toast = inject(HotToastService);
  private readonly _fb = inject(FormBuilder);

  constructor() {
    super();
    this.roomTypes = EnumToKeyValueArray(RoomType, this._translate);
    this.days = DateTimeUtility.getWeekDays(this._translate) as DropDownItem[];
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ----------------------------------------------------- GETTERS -------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  get isUserAdmin() {
    return this._permissionService.isAdmin();
  }

  get isUserCreator(): boolean {
    return +this.currentRoom?.author === +GetUserId();
  }

  get weekdaysFormArray() {
    return this.form.get('weekdays') as FormArray;
  }

  get allDaysSelected() {
    return this.weekdaysFormArray.length === this.days.length;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------------------ ACTIONS ------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  onPaginationChange(pagination: Pagination) {
    this.pagination = pagination;

    if (this.isScheduleView) {
      this.fetchRoomsForScheduleView();
    } else {
      this.fetchRooms();
    }

    scrollToTop(!isMobileView() ? '.rooms-wrapper' : null);
  }

  deleteRoom(room: RoomEntity) {
    if (!room.id) return;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheet.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translate.instant('Room') + ' ' + room.name,
        },
      });

      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this._deleteRoom(room.id);
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.DELETE,
          emphasize: room.name,
        },
      });
      confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (result: any) => {
          if (result) {
            this._deleteRoom(room.id);
          }
        },
      });
    }
  }

  addWeekdaysRow(data?: { weekday: string; timeFrom: string; timeTo: string }) {
    const weekdaysAvailability = this.weekdaysFormArray;
    const newGroup = this._fb.group(
      {
        day: [+data?.weekday, [Validators.required]],
        time_from: [data?.timeFrom || null, [Validators.required]],
        time_to: [data?.timeTo || null, [Validators.required]],
      },
      { validators: timeRangeValidator('time_from', 'time_to') },
    );

    weekdaysAvailability.push(newGroup);

    weekdaysAvailability.controls.forEach((group) => {
      const dayControl = group.get('day');
      if (dayControl) {
        dayControl.setValidators([Validators.required]);
        dayControl.updateValueAndValidity({ emitEvent: false });
        dayControl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  removeWeekdaysRow(index: number) {
    const weekdays = this.form.get('weekdays') as FormArray;
    weekdays.removeAt(index);
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // -------------------------------------------- FETCH DATA Functions ---------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  fetchRooms() {
    this.call(this._useRoom.getAllRooms(this.pagination, this.filterQuery))
      .then((res: RoomsListEntity) => {
        if (res) {
          this.rooms = res.rooms;
          this.pagination = res.pagination;
        } else {
          log.info('No Rooms found');
        }
      })
      .catch((err) => {
        log.error('Error while fetching Rooms', err);
      });
  }

  fetchRoomsForScheduleView() {
    this.isLoadingForSchedule = true;
    this._useRoom
      .getAllRooms(this.pagination, this.filterQuery)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: RoomsListEntity) => {
          if (res) {
            this.rooms = res.rooms;
            this.pagination = res.pagination;
            this._fetchRoomDetailsForScheduleView();
          } else {
            log.info('No Rooms found');
          }
        },
        error: (err) => {
          log.error('Error while fetching Rooms', err);
        },
      });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _InitDataForForm() {
    this._initForm();
    this.addWeekdaysRow();
    this._fetchTags();
  }

  protected _fetchRoomDetails(roomId: number) {
    this.call(this._useRoom.getRoomById(roomId))
      .then((res) => {
        if (res) {
          this.currentRoom = res;
          // this._patchFormValues(res);
        } else {
          log.info('No Room found');
        }
      })
      .catch((err) => {
        log.error('Error while fetching Room', err);
      });
  }

  protected _fetchRoomDetailsForScheduleView() {
    const detailRequests = this.rooms.map((room) => this._useRoom.getRoomById(room.id));

    forkJoin(detailRequests)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (rooms) => {
          this.rooms = rooms;
          const { columns, items } = convertRoomsToScheduleData(this.rooms);
          this.roomScheduleColumns = columns;
          this.roomScheduleItems = items;
          this.isLoadingForSchedule = false;
        },
        error: (err) => {
          log.error('Error while fetching Room details for Schedule View', err);
        },
      });
  }

  protected _createRoom(room: RoomEntity, btnRef?: ButtonComponent) {
    room.author = GetUserId();
    room.approvedStatus = this._permissionService.isAdmin() || this._permissionService.isFunctionary() ? 1 : 0;
    room.teamId = GetTeamId();
    const body = removeNullAndUndefinedProperties(instanceToPlain(new RoomEntity(room)));
    delete body.roomTags;
    delete body.room_availablity;

    const formData = this.jsonToFromData(body, ['tags', 'weekdays']);

    if (this.selectedImage) {
      formData.append('file', this.selectedImage);
    }

    const service$ = this.currentRoom && this.currentRoom.id ? this._useRoom.updateRoom(this.currentRoom.id, formData) : this._useRoom.createRoom(formData);

    this.call(service$, btnRef)
      .then((response) => {
        if (response) {
          if (!this._permissionService.isAdmin()) {
            this._toast.success(this._translate.instant('Your current Request is waiting for the Approval by Admin'), { duration: 6000 });
          } else {
            const message = this.currentRoom && this.currentRoom?.id ? 'Update Successful' : 'Created Successfully';
            this._toast.success(this._translate.instant(message));
          }

          this._socketService.emit(SocketEvents.SendNotification, NotificationType.Room);
          if (!isMobileView()) {
            this._dialogRef.close(true);
            this._dialogService.closeAll();
          } else {
            this._bottomSheet.closeAll();
          }
          this._reloadCurrentRoute();
        } else {
          this._toast.error(this._translate.instant('Something went wrong'));
        }
      })
      .catch((err) => {
        this._toast.error(this.error, { duration: 5000 });
        log.error('Error while creating or updating room');
      });
  }

  protected _patchFormValues(room: RoomEntity) {
    const tags = room.roomTags.map((tag) => tag.tagName);
    this.form.patchValue({
      name: room.name,
      email: room.email,
      address: room.address,
      noOfPersons: room.noOfPersons,
      description: room.description,
      type: room.type,
      phoneNo: room.phoneNo,
      tags,
      image: room.imageUrl,
      price: room.price || 0,
    });

    if (room.availability && room.availability.length) {
      this.weekdaysFormArray.clear();
      room.availability.forEach((day) => {
        this.addWeekdaysRow(day);
      });
    }
  }

  private _fetchTags() {
    this._useTags
      .getAllTagsByType(TagType.ROOM)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res && res.length) {
            this.roomsTags = res.map((tag) => ({ label: tag.tagName, value: tag.tagName }));
          }
        },
      });
  }

  private _deleteRoom(roomId: number) {
    this._useRoom
      .deleteRoom(roomId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this._toast.success(this._translate.instant('Delete Successful'));

          if (isMobileView() && this._bottomSheet) {
            this._bottomSheet.closeAll();
          } else if (this._dialogRef) {
            this._dialogRef.close();
          }

          this._socketService.emit(SocketEvents.SendNotification, NotificationType.Room);
          // this._reloadCurrentRoute();
        },
        error: (error) => {
          this._toast.error(error.error.message || this._translate.instant('Something went wrong'));
        },
      });
  }

  private _initForm() {
    this.form = this._fb.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      email: ['', [Validators.email]],
      address: [''],
      price: [0],
      noOfPersons: [null, [Validators.required, Validators.maxLength(4)]],
      description: ['', [Validators.maxLength(255)]],
      weekdays: this._fb.array([]),
      tags: [[]],
      type: [null, [Validators.required]],
      phoneNo: ['', [Validators.maxLength(14)]],
      image: [null],
    });
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }
}
